import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";

import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Spinner,
  Alert, Button
} from "reactstrap";

const EmployeeBenefitSummary = () => {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchEnrolledPlans = async () => {
      try {
        const res = await api.get("/employee-benefits");
        setPlans(res.data || []);
      } catch (err) {
        console.error("Failed to fetch enrolled plans", err);
        setError("Could not load enrolled benefit plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchEnrolledPlans();
  }, []);

  const grouped = plans.reduce((acc, plan) => {
    if (!acc[plan.type]) acc[plan.type] = [];
    acc[plan.type].push(plan);
    return acc;
  }, {});

  const formatDollar = (value) =>
    !value || parseFloat(value) === 0 ? "-" : `$${parseFloat(value).toFixed(2)}`;

  const calculateEmployeeCost = (total, employerShare) => {
    if (!total || !employerShare) return "-";
    const cost = parseFloat(total) * (1 - employerShare / 100);
    return `$${cost.toFixed(2)}`;
  };

  if (loading)
    return (
      <p className="text-center mt-5">
        <Spinner /> Loading enrolled benefits...
      </p>
    );

  return (
    <Card className="shadow-lg mt-4">
      <CardHeader style={{textAlign:'center'}} tag="h4">Your Enrolled Benefit Plans</CardHeader>
      <div className="text-end mb-3">
        <Button color="primary" onClick={() => navigate("/emp-dashboard/emp-deductions")}>
          Add/Change Policy
        </Button>
      </div>

      <CardBody>
        {error && <Alert color="danger">{error}</Alert>}

        {Object.keys(grouped).length === 0 ? (
          <p className="text-muted">No benefit plans selected.</p>
        ) : (
          Object.keys(grouped).map((type) => (
            <div key={type} className="mb-5">
              <h5 className="text-capitalize mb-3">{type} Plan</h5>

              {grouped[type].map((plan) => {
                const tiers = [
                  { label: "Employee Only", selected: plan.selected_employee_only, cost: plan.cost_employee_only },
                  { label: "Employee + Spouse", selected: plan.selected_employee_spouse, cost: plan.cost_employee_spouse },
                  { label: "Employee + Children", selected: plan.selected_employee_children, cost: plan.cost_employee_children },
                  { label: "Family", selected: plan.selected_family, cost: plan.cost_family }
                ].filter(t => t.selected);

                return (
                  <div key={plan.id} className="mb-4 border p-3 rounded shadow-sm">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h6 className="mb-1">{plan.plan_name}</h6>
                        <p className="mb-0">
                          {plan.carrier_name}
                          {plan.logo_url && (
                            <img
                              src={plan.logo_url}
                              alt="logo"
                              height={24}
                              className="ms-2"
                            />
                          )}
                        </p>
                      </div>
                      <small className="text-muted">Employer Covers {plan.employer_contribution}%</small>
                    </div>

                    <Table bordered responsive size="sm">
                      <thead>
                        <tr>
                          <th>Coverage Tier</th>
                          <th>Total Cost</th>
                          <th>Your Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiers.map((tier) => (
                          <tr key={tier.label}>
                            <td>{tier.label}</td>
                            <td>{formatDollar(tier.cost)}</td>
                            <td>{calculateEmployeeCost(tier.cost, plan.employer_contribution)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
};

export default EmployeeBenefitSummary;
